import { _ as _assign } from '../tslib.es6-c6da0ae6.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useContext, createContext, useState, useMemo, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { AbsolutesBlackColors, PositiveColors } from '@picsart/design-system/foundation/colors';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import { Timings } from '@picsart/design-system/foundation/timings';
import { Easing } from '@picsart/design-system/foundation/easing';
import { CornerRadius } from '@picsart/design-system/foundation/cornerRadius';
import { createUseStyles } from 'react-jss';
import { Badge, BadgeTypes } from '@picsart/design-system/components/Badge';
import { M as Media } from '../Media-e137048e.js';
import '@picsart/rc/enums';
import '../isBrowser/index.js';
import '@picsart/design-system/foundation/icons/IconArrowLeftFilled';
import '@picsart/design-system/foundation/icons/IconArrowRightFilled';
import { Button, ButtonSkinAbsoluteWhite, ButtonElementTypes } from '@picsart/design-system/components/Button';
import { IconEdit } from '@picsart/design-system/foundation/icons/IconEdit';
import { useTranslator } from '@picsart/rc/services/localization';
import addAnalyticsParamsToUrl from '../addAnalyticsParamsToUrl/index.js';
import { TypographyT2 } from '@picsart/design-system/foundation/typography';
import { IconPlay } from '@picsart/design-system/foundation/icons/IconPlay';
import { Text, TextElementTypes } from '@picsart/design-system/components/Text';
import { FontWeights } from '@picsart/design-system/foundation/fonts';

var TemplateContext = /*#__PURE__*/createContext(null);
var useTemplateContext = function useTemplateContext() {
  var context = useContext(TemplateContext);
  if (context === null) {
    throw new Error('useTemplateContext must be used within a TemplateProvider');
  }
  return context;
};

var useStyles$3 = createUseStyles({
  root: {
    borderRadius: CornerRadius.r12,
    cursor: 'pointer',
    overflow: 'overlay',
    position: 'relative',
    width: 'fit-content'
  },
  overlay: {
    position: 'absolute',
    left: Spacings.s0,
    top: Spacings.s0,
    width: '100%',
    height: 72,
    opacity: 0,
    backgroundImage: "linear-gradient(0deg, transparent 0%, ".concat(AbsolutesBlackColors.base["default"], " 200%);"),
    textAlign: 'right'
  },
  overlayOver: {
    opacity: 1
  },
  action: {
    position: 'relative',
    top: -60,
    transition: "top ".concat(Timings["default"], " ").concat(Easing.linear)
  },
  actionOver: {
    top: 16,
    right: 16
  }
}, {
  name: 'Template'
});

var TemplateContainer = function TemplateContainer(_a) {
  var children = _a.children,
    template = _a.template,
    containerLink = _a.containerLink,
    buttonLink = _a.buttonLink,
    buttonLabel = _a.buttonLabel,
    className = _a.className;
  var classes = useStyles$3();
  var _b = useState(false),
    isMouseOver = _b[0],
    setIsMouseOver = _b[1];
  var value = useMemo(function () {
    return {
      isMouseOver: isMouseOver,
      template: template,
      containerLink: containerLink,
      buttonLink: buttonLink,
      buttonLabel: buttonLabel
    };
  }, [isMouseOver, template, containerLink, buttonLink, buttonLabel]);
  return jsx(TemplateContext.Provider, _assign({
    value: value
  }, {
    children: jsx("div", _assign({
      "data-testid": "template",
      "data-pulse-section": "template",
      className: classNames(classes.root, className),
      onMouseEnter: function onMouseEnter() {
        return setIsMouseOver(true);
      },
      onMouseLeave: function onMouseLeave() {
        return setIsMouseOver(false);
      }
    }, {
      children: children
    }))
  }));
};

var useStyles$2 = createUseStyles({
  root: {
    position: 'relative'
  },
  hidden: {
    visibility: 'none'
  },
  badge: {
    position: 'absolute',
    right: Spacings.s16,
    bottom: Spacings.s16,
    zIndex: 1
  },
  img: {
    borderRadius: CornerRadius.r12,
    maxWidth: '100%',
    verticalAlign: 'bottom'
  }
}, {
  name: 'template-image'
});

var TemplateImage = function TemplateImage() {
  var _a;
  var classes = useStyles$2();
  var _b = useTemplateContext(),
    template = _b.template,
    isMouseOver = _b.isMouseOver;
  var id = template.id,
    _id = template._id;
  var videoUrl = (_a = template.animated_preview_info) === null || _a === void 0 ? void 0 : _a.url;
  var showVideo = videoUrl && isMouseOver;
  return jsxs("div", _assign({
    className: classes.root,
    "data-pulse-name": id || _id,
    "data-pulse-clickable": true
  }, {
    children: [jsx(Media, {
      imageSrc: template.url,
      alt: "Template ".concat(template.display_name),
      className: classNames(classes.img, showVideo && classes.hidden)
    }), template.is_paid && jsx("div", _assign({
      className: classes.badge
    }, {
      children: jsx(Badge, {
        variant: BadgeTypes.PlusCrown
      })
    }))]
  }));
};

var TryButton = function TryButton(_a) {
  var className = _a.className,
    _b = _a.showIcon,
    showIcon = _b === void 0 ? false : _b;
  var classes = useStyles$3();
  var translate = useTranslator();
  var _c = useTemplateContext(),
    isMouseOver = _c.isMouseOver,
    buttonLink = _c.buttonLink,
    template = _c.template,
    buttonLabel = _c.buttonLabel;
  var _id = template._id,
    id = template.id;
  var defaultButtonLink = addAnalyticsParamsToUrl("/create/editor?template=".concat(id || _id), {
    source: 'try-out-button',
    origin: 'templates'
  });
  return jsx("div", _assign({
    className: classNames(className || classes.overlay, isMouseOver && classes.overlayOver)
  }, {
    children: jsx(Button, _assign({
      skin: ButtonSkinAbsoluteWhite,
      className: classNames(classes.action, isMouseOver && classes.actionOver),
      tag: ButtonElementTypes.Link,
      href: buttonLink || defaultButtonLink,
      target: "_blank",
      "data-pulse-name": id,
      "data-testid": "button-link"
    }, {
      children: showIcon ? jsx(IconEdit, {
        fill: "#fff"
      }) : translate(buttonLabel)
    }))
  }));
};

function formatDuration(duration) {
  if (duration < 60) {
    return "0:".concat(String(duration).padStart(2, '0'));
  }
  return "".concat(Math.floor(duration / 60), ":").concat(String(duration % 60).padStart(2, '0'));
}
function getExtensionFromName(name) {
  var parts = name.split('.');
  return parts[parts.length - 1];
}

var useStyles$1 = createUseStyles({
  root: {
    background: 'rgba(38, 38, 38, 0.60)',
    borderRadius: CornerRadius.r24,
    display: 'flex',
    gap: Spacings.s2,
    padding: [Spacings.s2, Spacings.s8],
    width: 'fit-content',
    position: 'absolute',
    bottom: Spacings.s16,
    left: Spacings.s16
  }
}, {
  name: 'video-duration'
});

function VideoDuration() {
  var classes = useStyles$1();
  var _a = useTemplateContext(),
    isMouseOver = _a.isMouseOver,
    duration = _a.template.duration;
  var showDuration = duration && duration > 0 && !isMouseOver;
  return showDuration ? jsxs("div", _assign({
    className: classes.root,
    "data-testid": "vid-duration"
  }, {
    children: [jsx(IconPlay, {
      width: "12",
      height: "12",
      fill: "#fff"
    }), jsx(Text, _assign({
      elementType: TextElementTypes.Span,
      color: PositiveColors.base["default"],
      size: TypographyT2,
      weight: FontWeights.semibold
    }, {
      children: formatDuration(duration)
    }))]
  })) : null;
}

var useStyles = createUseStyles({
  root: {
    position: 'absolute',
    top: Spacings.s0,
    left: Spacings.s0,
    borderRadius: CornerRadius.r12,
    maxWidth: '100%',
    maxHeight: '100%'
  }
}, {
  name: 'video-preview'
});

function getMimeTypeFromSrc(src) {
  return "video/".concat(getExtensionFromName(src));
}
function VideoPreview() {
  var videoRef = useRef(null);
  var classes = useStyles();
  var translate = useTranslator();
  var _a = useTemplateContext(),
    template = _a.template,
    isMouseOver = _a.isMouseOver;
  var videoUrl = (template.animated_preview_info || {}).url;
  var showVideo = videoUrl && isMouseOver;
  useEffect(function () {
    if ((videoRef === null || videoRef === void 0 ? void 0 : videoRef.current) && showVideo) {
      var promise = videoRef.current.play();
      promise["catch"](function (err) {
        console.error('Play error:', err);
      });
    }
  }, [showVideo]);
  return showVideo ? jsxs("video", _assign({
    className: classes.root,
    ref: videoRef,
    loop: true,
    muted: true,
    "data-testid": "vid-preview"
  }, {
    children: [jsx("source", {
      src: videoUrl,
      type: getMimeTypeFromSrc(videoUrl)
    }), translate('Your browser does not support the video tag.')]
  })) : null;
}

function Template(_a) {
  var className = _a.className,
    template = _a.template,
    containerLink = _a.containerLink,
    buttonLink = _a.buttonLink,
    _b = _a.buttonLabel,
    buttonLabel = _b === void 0 ? 'Try it out' : _b;
  var id = template.id,
    _id = template._id;
  var templateId = id || _id;
  var defaultButtonLink = "/create/editor?template=".concat(templateId);
  return jsxs(TemplateContainer, _assign({
    className: className,
    template: template,
    containerLink: containerLink,
    buttonLink: buttonLink,
    buttonLabel: buttonLabel
  }, {
    children: [jsx("a", _assign({
      href: containerLink || defaultButtonLink,
      "data-testid": "container-link"
    }, {
      children: jsx(TemplateImage, {})
    })), jsx(TryButton, {}), jsx("a", _assign({
      href: containerLink || defaultButtonLink,
      "data-pulse-name": templateId
    }, {
      children: jsx(VideoPreview, {})
    })), jsx(VideoDuration, {})]
  }));
}

export { TemplateContainer, TemplateImage, TryButton, VideoDuration, VideoPreview, Template as default };
//# sourceMappingURL=index.js.map
